import { useState } from "react";
import { ActionList, ActionMenu, IconButton, Select, Text } from "@primer/react";
import { PencilIcon, PlusIcon, TrashIcon } from "@primer/octicons-react";
import { Alert } from "../components/Alert/Alert.jsx";
import { AssetTicker, assetHref } from "../components/AssetIdentity/AssetIdentity.jsx";
import { Button } from "../components/Button/Button.jsx";
import { ClearableTextInput } from "../components/ClearableTextInput/ClearableTextInput.jsx";
import { ComplianceResultGroups, complianceResultTone } from "../components/ComplianceResultGroups/ComplianceResultGroups.jsx";
import { ComplianceResultItem } from "../components/ComplianceResultItem/ComplianceResultItem.jsx";
import { formatPercentage } from "../components/EditablePercentage/EditablePercentage.jsx";
import { FormBody, FormContainer, FormFooter } from "../components/FormContainer/FormContainer.jsx";
import { PageHeading } from "../components/PageHeading/PageHeading.jsx";
import { PolicyFinding } from "../components/PolicyFinding/PolicyFinding.jsx";
import { PolicyIdentity, policyHref } from "../components/PolicyIdentity/PolicyIdentity.jsx";
import { propertyValueHref } from "../components/PropertyValueIdentity/PropertyValueIdentity.jsx";
import { amountForBasisPoints, formatPortfolioAmount, usePortfolioValue } from "../components/PortfolioValue/PortfolioValue.jsx";
import { Section, SectionStack } from "../components/Section/Section.jsx";
import { evaluateCompliance } from "../domain/compliance.js";
import {
  createAsset, deleteAsset, effectiveValue, updateAsset, validateAssetDraft, visiblePropertyValues
} from "../domain/document.js";
import { navigateToHash } from "../lib/navigateToHash.js";
import { useApplicationDocument } from "../storage/useApplicationDocument.js";
import "./AssetPage.css";

export function AssetPage({ assetId, repository }) {
  const application = useApplicationDocument(repository);
  if (application.error && !application.document) return <Alert tone="danger" heading="Unable to open Regulator">{application.error.message}</Alert>;
  if (!application.document) return <AssetLoading />;
  return <AssetContent application={application} assetId={assetId} />;
}

function AssetContent({ application, assetId }) {
  const { document } = application;
  const isNew = assetId === "new";
  const asset = isNew ? null : findAsset(document.assets, assetId);
  const [editing, setEditing] = useState(isNew);
  const [draft, setDraft] = useState(() => isNew ? { id: "", name: "", assignments: [] } : null);
  const [saveError, setSaveError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!isNew && !asset) return <MissingAsset assetId={assetId} />;

  function startEditing() {
    setDraft({
      ...asset,
      assignments: document.assignments
        .filter((assignment) => assignment.assetId === asset.id)
        .map(({ propertyId, valueId }) => ({ propertyId, valueId }))
    });
    setSaveError(null);
    setConfirmDelete(false);
    setEditing(true);
  }

  function cancelEditing() {
    if (isNew) {
      navigateToHash("#/assets");
      return;
    }
    setEditing(false);
    setDraft(null);
    setSaveError(null);
    setConfirmDelete(false);
  }

  async function saveAsset() {
    setSaveError(null);
    try {
      const nextDocument = isNew ? createAsset(document, draft) : updateAsset(document, asset.id, draft);
      if (await application.save(nextDocument)) {
        const savedId = draft.id.trim();
        if (isNew || savedId !== asset.id) navigateToHash(assetHref(savedId));
        else {
          setEditing(false);
          setDraft(null);
        }
      }
    } catch (error) {
      setSaveError(error.message);
    }
  }

  async function removeAsset() {
    if (await application.save(deleteAsset(document, asset.id))) navigateToHash("#/assets");
  }

  const allocation = asset ? document.allocations.find((item) => item.assetId === asset.id)?.basisPoints ?? 0 : 0;
  const hasChanges = assetDraftHasChanges(document, draft, asset?.id);

  return <article className="asset-page">
    <PageHeading
      description={asset ? <AssetPageSummary allocation={allocation} assetId={asset.id} /> : "Define the investment and its property values."}
      actions={!editing ? <Button tone="secondary" leadingVisual={PencilIcon} onClick={startEditing} labelDetail="asset">Edit</Button> : null}
    >{asset?.name || asset?.id || "New asset"}</PageHeading>
    {application.error ? <Alert tone="danger" heading="Changes were not saved">{application.error.message}</Alert> : null}
    {editing ? <AssetEditor document={document} draft={draft} existingAssetId={asset?.id} hasChanges={hasChanges} onCancel={cancelEditing} onChange={setDraft} onDelete={asset ? () => setConfirmDelete(true) : null} onSave={saveAsset} saveError={saveError} saving={application.saving} /> : <SectionStack>
      <Section heading="Properties" description="Effective classifications for this asset.">
        <AssetProperties asset={asset} document={document} />
      </Section>
      <Section heading="Current compliance" description="Policies that apply directly or through this asset's classifications.">
        <AssetCompliance asset={asset} document={document} />
      </Section>
    </SectionStack>}
    {confirmDelete ? <DeleteAssetConfirmation asset={asset} onCancel={() => setConfirmDelete(false)} onConfirm={removeAsset} saving={application.saving} /> : null}
  </article>;
}

function AssetPageSummary({ allocation, assetId }) {
  return <span className="asset-page-summary"><AssetTicker assetId={assetId} /><Text>· {formatPercentage(allocation)}% allocation</Text></span>;
}

function AssetProperties({ asset, document }) {
  if (!document.properties.length) return <Alert heading="No properties defined">Add properties to classify this asset.</Alert>;
  const assignedPropertyIds = new Set(document.assignments.filter((assignment) => assignment.assetId === asset.id).map((assignment) => assignment.propertyId));
  return <dl className="asset-properties">{document.properties.map((property) => {
    const value = effectiveValue(document, asset.id, property.id);
    const inherited = !assignedPropertyIds.has(property.id);
    return <div key={property.id}><dt>{property.name}</dt><dd><a href={propertyValueHref(property.id, value.id)}>{value.name}</a>{inherited ? <Text>Default</Text> : null}</dd></div>;
  })}</dl>;
}

function AssetCompliance({ asset, document }) {
  const findings = evaluateCompliance(document).flatMap((evaluation) => evaluation.results
    .filter((result) => resultIncludesAsset(result, asset.id))
    .map((result) => ({ evaluation, key: `${evaluation.policy.id}:${result.key}`, result, tone: complianceResultTone(result.status) })));
  return <ComplianceResultGroups
    defaultExpanded={[]}
    empty="No policy results apply to this asset."
    expandedEdge
    findings={findings}
    renderFinding={(finding) => <AssetComplianceResult document={document} finding={finding} key={finding.key} />}
  />;
}

function AssetComplianceResult({ document, finding }) {
  const { value } = usePortfolioValue();
  const policy = finding.evaluation.policy;
  const bucket = policy.scope.type === "concentration" && policy.scope.dimension.type === "property"
    ? findingBucket(document, finding.result)
    : null;
  const summary = <><PolicyIdentity document={document} identityType={finding.result.subject.type === "asset" ? "asset" : "property"} policy={policy} variant="subject" />{bucket ? null : <AssetComplianceAllocation basisPoints={finding.result.basisPoints} portfolioValue={value} />}</>;
  const detail = <>{bucket ? <><Text className="asset-compliance-bucket">{bucket}</Text><AssetComplianceAllocation basisPoints={finding.result.basisPoints} portfolioValue={value} /></> : null}<PolicyFinding document={document} policy={policy} result={finding.result} showIdentity={false} /></>;
  return <ComplianceResultItem className="asset-compliance-result" detail={detail} href={policyHref(policy.id)} status={finding.result.status} summary={summary} />;
}

function AssetComplianceAllocation({ basisPoints, portfolioValue }) {
  return <Text className="asset-compliance-allocation">({formatPercentage(basisPoints)}%{portfolioValue !== null ? <span className="portfolio-amount"> · {formatPortfolioAmount(amountForBasisPoints(portfolioValue, basisPoints))}</span> : null})</Text>;
}

function findingBucket(document, result) {
  const property = document.properties.find((item) => item.id === result.subject.propertyId);
  return property.values.find((item) => item.id === result.subject.valueId).name;
}

function resultIncludesAsset(result, assetId) {
  if (result.subject.type === "asset") return result.subject.asset?.id === assetId;
  return result.subject.assetIds.includes(assetId);
}

function AssetEditor({ document, draft, existingAssetId, hasChanges, onCancel, onChange, onDelete, onSave, saveError, saving }) {
  const validationError = validateAssetDraft(document, draft, existingAssetId);
  const assignedPropertyIds = new Set(draft.assignments.map((assignment) => assignment.propertyId));
  const explicitProperties = document.properties.filter((property) => assignedPropertyIds.has(property.id));
  const availableProperties = document.properties.filter((property) => !assignedPropertyIds.has(property.id));

  function selectProperty(propertyId, valueId) {
    const assignments = draft.assignments.filter((item) => item.propertyId !== propertyId);
    if (valueId) assignments.push({ propertyId, valueId });
    onChange({ ...draft, assignments });
  }

  function addProperty(property) {
    onChange({ ...draft, assignments: [...draft.assignments, { propertyId: property.id, valueId: property.values[0].id }] });
  }

  return <FormContainer className="asset-editor">
    <FormBody>
      <div className="asset-fields">
        <label><Text className="editor-label">Asset ID / ticker</Text><ClearableTextInput clearLabel="Clear asset ID" value={draft.id} onClear={() => onChange({ ...draft, id: "" })} autoFocus onChange={(event) => onChange({ ...draft, id: event.target.value })} block /></label>
        <label><Text className="editor-label">Asset name</Text><ClearableTextInput clearLabel="Clear asset name" value={draft.name} onClear={() => onChange({ ...draft, name: "" })} onChange={(event) => onChange({ ...draft, name: event.target.value })} block /></label>
      </div>
      {document.properties.length ? <div className="asset-property-fields">
        <Text className="asset-properties-heading">Properties</Text>
        {explicitProperties.length ? <div className="asset-property-list">{explicitProperties.map((property) => {
          const assignment = draft.assignments.find((item) => item.propertyId === property.id);
          const visibleValues = visiblePropertyValues(document, draft.assignments, property.id);
          const currentValue = property.values.find((value) => value.id === assignment.valueId);
          const currentValueHidden = currentValue && !visibleValues.some((value) => value.id === currentValue.id);
          const options = currentValueHidden ? [currentValue, ...visibleValues] : visibleValues;
          return <div className="asset-property-row" key={property.id}><label><Text>{property.name}</Text><Select value={assignment.valueId} onChange={(event) => selectProperty(property.id, event.target.value)} block>{options.map((value) => <Select.Option key={value.id} value={value.id}>{value.name}{currentValueHidden && value.id === currentValue.id ? " — hidden by current criteria" : ""}</Select.Option>)}</Select></label><IconButton className="asset-property-remove" icon={TrashIcon} variant="invisible" aria-label={`Remove ${property.name} override`} onClick={() => selectProperty(property.id, "")} /></div>;
        })}</div> : null}
        {availableProperties.length ? <ActionMenu><ActionMenu.Button className="add-asset-property" leadingVisual={PlusIcon}>Add property</ActionMenu.Button><ActionMenu.Overlay><ActionList>{availableProperties.map((property) => <ActionList.Item key={property.id} onSelect={() => addProperty(property)}>{property.name}</ActionList.Item>)}</ActionList></ActionMenu.Overlay></ActionMenu> : null}
      </div> : <Alert heading="No properties defined">This asset can be saved now and classified after properties are added.</Alert>}
    </FormBody>
    <FormFooter className="asset-editor-footer" hasChanges={hasChanges}>
      <div><Button onClick={onSave} disabled={!hasChanges || Boolean(validationError) || saving} labelDetail={saving ? undefined : "asset"}>{saving ? "Saving…" : "Save"}</Button><Button tone="quiet" onClick={onCancel}>Cancel</Button></div>
      <Text className={`asset-editor-message${saveError ? " error" : ""}`} role={saveError ? "alert" : "status"}>{saveError ?? validationError}</Text>
      {onDelete ? <Button className="form-danger-action" tone="danger" leadingVisual={TrashIcon} onClick={onDelete} labelDetail="asset">Delete</Button> : null}
    </FormFooter>
  </FormContainer>;
}

function assetDraftHasChanges(document, draft, existingAssetId) {
  if (!draft) return false;
  if (!existingAssetId) return Boolean(draft.id || draft.name || draft.assignments.length);
  const asset = document.assets.find((item) => item.id === existingAssetId);
  const assignments = document.assignments.filter((assignment) => assignment.assetId === existingAssetId);
  if (!asset || draft.id !== asset.id || draft.name !== asset.name || draft.assignments.length !== assignments.length) return true;
  return draft.assignments.some((draftAssignment) => !assignments.some((assignment) => (
    assignment.propertyId === draftAssignment.propertyId && assignment.valueId === draftAssignment.valueId
  )));
}

function DeleteAssetConfirmation({ asset, onCancel, onConfirm, saving }) {
  return <div className="asset-delete"><Alert tone="warning" heading={`Delete “${asset.id}”?`}>This removes {asset.name} and all of its property assignments.</Alert><div><Button tone="quiet" onClick={onCancel}>Cancel</Button><Button tone="danger" onClick={onConfirm} disabled={saving} labelDetail={saving ? undefined : "asset"}>{saving ? "Deleting…" : "Delete"}</Button></div></div>;
}

function MissingAsset({ assetId }) {
  return <article className="asset-page"><PageHeading description="This asset may have been renamed or deleted.">Asset not found</PageHeading><Alert tone="warning" heading={`Asset “${assetId}” was not found`}><a href="#/assets">Return to Assets</a></Alert></article>;
}

function AssetLoading() {
  return <article className="asset-page"><PageHeading description="Loading asset…">Asset</PageHeading></article>;
}

function findAsset(assets, assetId) {
  return assets.find((asset) => asset.id.toLocaleLowerCase() === assetId.toLocaleLowerCase()) ?? null;
}
